import React, { useEffect, useState } from 'react';
import Typography from '../../../../components/atoms/typography';
import { Box, Grid, Modal } from '@material-ui/core';
import InputDisabled from '../../../../components/atoms/inputDisabled';
import ButtonComponent from '../../../../components/atoms/button';
import { Controller, useFormContext } from 'react-hook-form';
import { useRegisterFreights } from '../../../../hooks/freights/useRegisterFreights';
import ReorderRoute from '../../../../components/reorderList';
import AlertWarning from '../../../../components/atoms/alert';
import {
  freightTypeEnum,
  useConfigureFreight,
} from '../../../../hooks/freights/useConfigureFreight';
import moment from 'moment';
import { Row } from '../../../../hooks/carts/useNearGetCarts';
import useGetDeliveriesRequests from '../../../../hooks/delivery-requests';
import useNearGetCarts from '../../../../hooks/carts/useNearGetCarts';
import jwtDecode from 'jwt-decode';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { FreightDetails } from '../configureFreight';
import { DeliveryRequest } from '../../../../store/ducks/freight/types';
import ModalCustomizeFreight from '../../../organisms/modal';
import { useUpdateCustomPriceFreight } from '../../../../hooks/freights/useUpdateCustomPriceFreight';
import { useUpdateFreight } from '../../../../hooks/freights/useUpdateFreight';
import ModalCustom from '../../../atoms/modalCustom';
import { defaultFormatKilometrageNumbers } from '../../../../utils/formatKilometrageNumber';

interface checkImportProps {
  onNextStep: () => void;
  onPrevStep: (customStep?: number) => void;
  publishFreight?: boolean;
  setPublishFreight?: React.Dispatch<React.SetStateAction<boolean>>;
  loading?: boolean;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  mode?: 'edit' | 'create';
  freightId?: string;
}

const CheckImport: React.FC<checkImportProps> = ({
  onNextStep,
  onPrevStep,
  setPublishFreight,
  setLoading: setParentLoading,
  mode,
  freightId: propFreightId,
}) => {
  const { control, getValues, setValue, reset, watch } =
    useFormContext<FreightDetails>();
  const freightDetails = getValues();

  const totalDeliveries = watch('totalDeliveries');
  const totalWeight = watch('totalWeight');
  const totalValue = watch('totalValue');
  const freightCost = watch('freightCost');
  const totalKilometers = watch('totalKilometers');
  const freightType = watch('freightType');
  const percentageFreightCost = watch('percentageFreightCost');
  const token = useSelector(
    (state: ApplicationState) => state.distributor.token,
  );
  const tokenDecoded = jwtDecode<{ distributorId: string }>(token);

  const [modalOpen, setModalOpen] = useState(false);
  const { execute: registerFreight, loading: registerFreightLoading } =
    useRegisterFreights();
  const { execute: configureFreight, loading: configureFreightLoading } =
    useConfigureFreight();
  const { execute: getDeliveryRequest } = useGetDeliveriesRequests();
  const { execute: getNearCarts } = useNearGetCarts();

  const [listReordered, setListReordered] = useState(false);
  const [deliveriesToReorder, setDeliveriesToReorder] = useState<
    Array<{
      id?: string;
      corporateName: string;
      address: string;
      totalWeight: number;
      locked: boolean;
    }>
  >([]);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [modalPublishOpen, setModalPublishOpen] = useState(false);

  const { execute, loading: customPriceLoading } =
    useUpdateCustomPriceFreight();
  const { execute: updateFreight, loading: updateFreightLoading } =
    useUpdateFreight();
  const [selectedValue, setSelectedValue] = useState<string>('option1');
  const isoDate = freightDetails.createdAt;
  const date = moment(isoDate);

  const handleReorder = (
    newOrder: Array<{
      id?: string;
      corporateName: string;
      address: string;
      totalWeight: number;
      locked: boolean;
    }>,
  ) => {
    setListReordered(true);
    setDeliveriesToReorder(newOrder);
  };

  const calculatePercent = (): number | null => {
    if (percentageFreightCost !== null && percentageFreightCost !== undefined) {
      return Number(percentageFreightCost) / 100;
    }

    if (Number(freightCost || 0) > 0 && Number(totalValue || 0) > 0) {
      const percentage = (Number(freightCost) / Number(totalValue)) * 100;
      return Math.round(percentage * 100) / 100;
    }

    return null;
  };

  const percentValue = calculatePercent();
  const percent =
    percentValue !== null
      ? percentValue.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '';

  const handleRecalculed = async () => {
    setModalOpen(true);
  };

  const handleOpenModal = () => {
    setModalPublishOpen(true);
  };

  const handlePublicFreightAction = () => {
    if (freightDetails.freightType === freightTypeEnum.INTERNAL) {
      handlePublishClick();
    } else {
      listReordered ? handleRecalculed() : handleOpenModal();
    }
  };

  const handleOpenModalBack = () => {
    setModalOpen(true);
  };

  const handleRecalculedFreight = async () => {
    try {
      setIsRecalculating(true);
      const waypointsNames = deliveriesToReorder.some(el => el.locked)
        ? deliveriesToReorder.map((item, index, array) => {
            if (item.locked) {
              const destinations = array
                .slice(index + 1)
                .map((_, subIndex) => `destination${index + 2 + subIndex}`)
                .join(',');
              return {
                corporateName: item.corporateName,
                before: destinations,
              };
            } else {
              return { corporateName: item.corporateName };
            }
          })
        : deliveriesToReorder.map(item => {
            return { corporateName: item.corporateName };
          });

      let percentageToSend: string | undefined = undefined;

      if (
        percent &&
        percent !== '0,00' &&
        percent !== '0.00' &&
        percent !== '' &&
        parseFloat(percent.replace(',', '.')) > 0
      ) {
        percentageToSend = percent;
      } else if (
        freightDetails.percentageFreightCost !== null &&
        freightDetails.percentageFreightCost !== undefined &&
        freightDetails.percentageFreightCost > 0
      ) {
        percentageToSend = (
          Number(freightDetails.percentageFreightCost) / 100
        ).toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }

      const extraCarts = freightDetails.extraCarts || [];
      const extraCartIds = freightDetails.extraCartIds || [];

      const extraCartsWeight = extraCarts.reduce(
        (sum, cart) => sum + (Number(cart.totalWeight) || 0),
        0,
      );
      const extraCartsDeliveries = extraCarts.length;

      const originalWeight =
        freightDetails.originalTotalWeight ||
        freightDetails.totalWeight - extraCartsWeight;
      const originalDeliveries = Math.max(
        0,
        (freightDetails.totalDeliveries || 0) - extraCartsDeliveries,
      );

      const totalWeightToSend = originalWeight + extraCartsWeight;
      const totalDeliveriesToSend = originalDeliveries + extraCartsDeliveries;
      const totalValueToSend = freightDetails.totalValue;

      const data = await configureFreight({
        freightRequestId: freightDetails.freightRequestId,
        totalWeight: totalWeightToSend,
        totalDeliveries: totalDeliveriesToSend,
        totalValue: totalValueToSend,
        numberOfHelpers: freightDetails.numberOfHelpers,
        waypoints: waypointsNames,
        pickUpDate: freightDetails.pickUpDate,
        pickUpTime: freightDetails.pickUpTime,
        freightType: freightDetails.freightType,
        vehicleTypeId: freightDetails.vehicleTypeId,
        extraCartIds: extraCartIds,
        percentageFreightCost: percentageToSend,
      });

      if (data) {
        const currentValues = getValues();

        reset(
          {
            ...currentValues,
            totalWeight:
              data.totalWeight !== undefined &&
              data.totalWeight !== null &&
              !isNaN(Number(data.totalWeight))
                ? Number(data.totalWeight)
                : currentValues.totalWeight,
            totalValue:
              data.totalValue !== undefined &&
              data.totalValue !== null &&
              !isNaN(Number(data.totalValue))
                ? Number(data.totalValue)
                : currentValues.totalValue,
            totalDeliveries:
              data.totalDeliveries !== undefined &&
              data.totalDeliveries !== null &&
              !isNaN(Number(data.totalDeliveries))
                ? Number(data.totalDeliveries)
                : currentValues.totalDeliveries,
            freightCost:
              data.freightCost !== undefined &&
              data.freightCost !== null &&
              !isNaN(Number(data.freightCost)) &&
              Number(data.freightCost) > 0
                ? Number(data.freightCost)
                : data.priceWithFee !== undefined &&
                    data.priceWithFee !== null &&
                    !isNaN(Number(data.priceWithFee)) &&
                    Number(data.priceWithFee) > 0
                  ? Number(data.priceWithFee)
                  : currentValues.freightCost,
            totalKilometers:
              data.totalKilometers !== undefined &&
              data.totalKilometers !== null &&
              !isNaN(Number(data.totalKilometers))
                ? Number(data.totalKilometers)
                : currentValues.totalKilometers,
          },
          { keepValues: false },
        );

        if (
          data.totalWeight !== undefined &&
          data.totalWeight !== null &&
          !isNaN(Number(data.totalWeight))
        ) {
          setValue('totalWeight', Number(data.totalWeight), {
            shouldValidate: true,
            shouldDirty: false,
          });
        }

        if (
          data.totalValue !== undefined &&
          data.totalValue !== null &&
          !isNaN(Number(data.totalValue))
        ) {
          setValue('totalValue', Number(data.totalValue), {
            shouldValidate: true,
            shouldDirty: false,
          });
        }

        if (
          data.totalDeliveries !== undefined &&
          data.totalDeliveries !== null &&
          !isNaN(Number(data.totalDeliveries))
        ) {
          setValue('totalDeliveries', Number(data.totalDeliveries), {
            shouldValidate: true,
            shouldDirty: false,
          });
        }

        if (
          data.freightCost !== undefined &&
          data.freightCost !== null &&
          !isNaN(Number(data.freightCost)) &&
          Number(data.freightCost) > 0
        ) {
          setValue('freightCost', Number(data.freightCost), {
            shouldValidate: true,
            shouldDirty: false,
          });
        } else if (
          data.priceWithFee !== undefined &&
          data.priceWithFee !== null &&
          !isNaN(Number(data.priceWithFee)) &&
          Number(data.priceWithFee) > 0
        ) {
          setValue('freightCost', Number(data.priceWithFee), {
            shouldValidate: true,
            shouldDirty: false,
          });
        }

        if (
          data.totalKilometers !== undefined &&
          data.totalKilometers !== null &&
          !isNaN(Number(data.totalKilometers))
        ) {
          setValue('totalKilometers', Number(data.totalKilometers), {
            shouldValidate: true,
            shouldDirty: false,
          });
        }

        sessionStorage.setItem('lastRecalculationTime', Date.now().toString());
      }

      if (setPublishFreight) {
        setPublishFreight(true);
      }
      setModalOpen(false);
      setListReordered(false);
      setIsRecalculating(false);
    } catch (error) {
      setIsRecalculating(false);
    }
  };

  const handlePrev = () => {
    reset({
      loadNumber: '',
      createdAt: new Date(),
      freightCost: 0,
      totalValue: 0,
      totalKilometers: 0,
      waypoints: [],
      deliveryRequests: [],
    });
    onPrevStep(2);
  };

  const handlePublishClick = async () => {
    // Fechar o modal imediatamente
    setModalPublishOpen(false);

    try {
      if (setParentLoading) setParentLoading(true);
      const updatedFreightCost = getValues('freightCost');
      const freightId = String(propFreightId || '');

      if (mode === 'edit' && freightId && freightId !== '') {
        const coercedFreightType =
          freightDetails.freightType === freightTypeEnum.PRIVATE &&
          (freightDetails as { vehicleId?: string }).vehicleId
            ? freightTypeEnum.INTERNAL
            : freightDetails.freightType;

        const updatePayload: {
          pickUpDate: string;
          deliveryLimitDate: string;
          numberOfHelpers: string;
          freightStatus: string;
          freightType: string;
          pickUpTime?: string;
          driverId?: string;
          vehicleId?: string;
        } = {
          pickUpDate: freightDetails.pickUpDate,
          deliveryLimitDate: freightDetails.deliveryLimitDate,
          numberOfHelpers: String(freightDetails.numberOfHelpers),
          freightStatus: 'PUBLISHED',
          freightType: coercedFreightType as string,
        };

        if (
          freightDetails.pickUpTime &&
          freightDetails.pickUpTime.trim() !== ''
        ) {
          updatePayload.pickUpTime = freightDetails.pickUpTime;
        }

        if ((freightDetails as { driverId?: string }).driverId) {
          updatePayload.driverId = (
            freightDetails as { driverId?: string }
          ).driverId;
        }
        if ((freightDetails as { vehicleId?: string }).vehicleId) {
          updatePayload.vehicleId = (
            freightDetails as { vehicleId?: string }
          ).vehicleId;
        }

        if (selectedValue === 'option2' && updatedFreightCost) {
          await execute(freightId, {
            freightCost: `${updatedFreightCost}`,
          });
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        await updateFreight(freightId, updatePayload);

        onNextStep();
        setTimeout(() => {
          if (setPublishFreight) {
            setPublishFreight(true);
          }
          if (setParentLoading) setParentLoading(false);
        }, 3000);
      } else {
        const response = await registerFreight({
          distributorId: tokenDecoded.distributorId,
          freightRequestId: freightDetails.freightRequestId,
        });

        if (response?.status === 201 && selectedValue === 'option2') {
          await execute(response?.data.freightId, {
            freightCost: `${updatedFreightCost}`,
          });
          onNextStep();
        } else {
          onNextStep();
        }

        setTimeout(() => {
          if (setPublishFreight) {
            setPublishFreight(true);
          }
          if (setParentLoading) setParentLoading(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error: ', error);
      if (setParentLoading) setParentLoading(false);
    }
  };

  const handleChangeSelectedValue = (value: string) => {
    setSelectedValue(value);
  };

  useEffect(() => {
    const fetchDeliveryRequestData = async () => {
      try {
        const data = await getDeliveryRequest(freightDetails.freightRequestId);

        let extraCartsData: Row[] = [];
        let extraCartIdsToUse: string[] = [];

        if (freightDetails.extraCarts && freightDetails.extraCarts.length > 0) {
          extraCartsData = freightDetails.extraCarts;
          extraCartIdsToUse =
            freightDetails.extraCartIds ||
            freightDetails.extraCarts.map(cart => cart.cartId);
        } else if (
          freightDetails.extraCartIds &&
          freightDetails.extraCartIds.length > 0
        ) {
          try {
            const firstPageResult = await getNearCarts({
              page: 1,
              rowsPerPage: 10,
              freightRequestId: freightDetails.freightRequestId,
            });

            if (firstPageResult && firstPageResult.totalRecords > 0) {
              const cartsResult = await getNearCarts({
                page: 1,
                rowsPerPage: firstPageResult.totalRecords,
                freightRequestId: freightDetails.freightRequestId,
              });

              if (cartsResult) {
                extraCartsData = cartsResult.rows.filter((cart: Row) =>
                  freightDetails.extraCartIds.includes(cart.cartId),
                );
                extraCartIdsToUse = freightDetails.extraCartIds;
              }
            }
          } catch (error) {
            // Error handling
          }
        } else if (data?.extraCartIds && data.extraCartIds.length > 0) {
          try {
            const firstPageResult = await getNearCarts({
              page: 1,
              rowsPerPage: 10,
              freightRequestId: freightDetails.freightRequestId,
            });

            if (firstPageResult && firstPageResult.totalRecords > 0) {
              const cartsResult = await getNearCarts({
                page: 1,
                rowsPerPage: firstPageResult.totalRecords,
                freightRequestId: freightDetails.freightRequestId,
              });

              if (cartsResult) {
                extraCartsData = cartsResult.rows.filter((cart: Row) =>
                  data.extraCartIds.includes(cart.cartId),
                );
                extraCartIdsToUse = data.extraCartIds;
              }
            }
          } catch (error) {
            // Error handling
          }
        }

        reset({
          ...freightDetails,
          loadNumber: data?.deliveryRequests[0]?.loadNumber,
          createdAt: data?.createdAt,
          freightCost: data?.freightCost,
          totalValue: data?.totalValue,
          totalKilometers: data?.totalKilometers,
          waypoints: data?.waypoints,
          deliveryRequests: data?.deliveryRequests,
          driver: data?.driver,
          vehicle: data?.vehicle,
          percentageFreightCost: data?.percentageFreightCost,
          extraCartIds:
            extraCartIdsToUse.length > 0
              ? extraCartIdsToUse
              : data?.extraCartIds || [],
          extraCarts:
            extraCartsData.length > 0
              ? extraCartsData
              : freightDetails.extraCarts || [],
        });
      } catch (error) {
        // Error handling
      }
    };

    if (freightDetails.freightRequestId) {
      fetchDeliveryRequestData();
    }
  }, [freightDetails.freightRequestId]);

  useEffect(() => {
    const unifiedItems: { [key: string]: Row } = {};

    (freightDetails.deliveryRequests || []).forEach((item: DeliveryRequest) => {
      const key = `dr-${item.deliveryRequestId}`;
      unifiedItems[key] = {
        corporateName: item.corporateName,
        distance: 0,
        address: item.address,
        cartId: item.deliveryRequestId,
        totalWeight: item.totalWeight,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      };
    });

    const extraCarts = freightDetails.extraCarts || [];
    const deliveryRequestIds = new Set(
      (freightDetails.deliveryRequests || []).map(
        (dr: DeliveryRequest) => dr.deliveryRequestId,
      ),
    );

    extraCarts.forEach((item: Row) => {
      if (deliveryRequestIds.has(item.cartId)) {
        return;
      }

      const key = `cart-${item.cartId}`;
      unifiedItems[key] = {
        corporateName: item.corporateName,
        distance: item.distance || 0,
        address: item.address,
        cartId: item.cartId,
        totalWeight: item.totalWeight,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      };
    });

    let sortedList: Row[] = [];
    if (freightDetails.waypoints && freightDetails.waypoints.length > 0) {
      const itemsByCorporateName: { [key: string]: Row[] } = {};
      Object.values(unifiedItems).forEach(item => {
        if (!itemsByCorporateName[item.corporateName]) {
          itemsByCorporateName[item.corporateName] = [];
        }
        itemsByCorporateName[item.corporateName].push(item);
      });

      freightDetails.waypoints.forEach(waypoint => {
        const items = itemsByCorporateName[waypoint] || [];
        sortedList.push(...items);
      });

      const waypointSet = new Set(freightDetails.waypoints);
      Object.values(unifiedItems).forEach(item => {
        if (!waypointSet.has(item.corporateName)) {
          sortedList.push(item);
        }
      });
    } else {
      sortedList = Object.values(unifiedItems);
    }

    const mappedList = sortedList.map(item => ({
      id: item.cartId,
      corporateName: item.corporateName,
      address: item.address,
      totalWeight: item.totalWeight,
      locked: false,
    }));

    setDeliveriesToReorder(mappedList);
  }, [
    freightDetails.waypoints,
    freightDetails.deliveryRequests,
    freightDetails.extraCarts,
  ]);

  const FreightCalculationDataTable: React.FC = () => {
    const gridItemSpace = freightType === freightTypeEnum.INTERNAL ? true : 4;

    // Usar key dinâmico para forçar re-renderização quando os valores mudarem
    const tableKey = `${totalWeight}-${totalValue}-${freightCost}-${totalDeliveries}-${totalKilometers}`;

    return (
      <Grid container spacing={2} style={{ marginTop: 2 }} key={tableKey}>
        <Grid item xs={gridItemSpace}>
          <Controller
            name="totalDeliveries"
            control={control}
            render={() => (
              <InputDisabled
                key={`deliveries-${totalDeliveries}`}
                label={'Total de entregas'}
                value={totalDeliveries || 0}
              />
            )}
          />
        </Grid>
        <Grid item xs={gridItemSpace}>
          <Controller
            name="totalWeight"
            control={control}
            render={({ field: { value, onChange } }) => {
              const weightValue = value || totalWeight || 0;
              const displayValue = weightValue.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
              return (
                <InputDisabled
                  key={`weight-${totalWeight}`}
                  label={'Peso total'}
                  value={displayValue}
                  onChange={onChange}
                  endAdornment={'kg'}
                />
              );
            }}
          />
        </Grid>
        <Grid item xs={gridItemSpace}>
          <Controller
            name="totalKilometers"
            control={control}
            render={() => (
              <InputDisabled
                key={`km-${totalKilometers}`}
                label={'Quilometragem estimada'}
                value={defaultFormatKilometrageNumbers(totalKilometers || 0)}
                endAdornment={'km'}
              />
            )}
          />
        </Grid>

        {freightType !== freightTypeEnum.INTERNAL && (
          <Grid item xs={gridItemSpace}>
            <InputDisabled
              key={`percent-${percent}`}
              size="small"
              label={'Percentual do frete'}
              value={percent}
              endAdornment={'%'}
            />
          </Grid>
        )}

        {freightType !== freightTypeEnum.INTERNAL && (
          <Grid item xs={gridItemSpace}>
            <Controller
              name="freightCost"
              control={control}
              render={() => (
                <InputDisabled
                  key={`cost-${freightCost}`}
                  label={'Custo do frete'}
                  value={Number(freightCost || 0).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  startAdornment={'R$'}
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={gridItemSpace}>
          <InputDisabled
            key={`value-${totalValue}`}
            label={'Valor total das mercadorias'}
            value={
              totalValue !== null && totalValue !== undefined
                ? Number(totalValue).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : ''
            }
            startAdornment={'R$'}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <ModalCustom
        open={modalOpen}
        label={'Você tem certeza que deseja voltar?'}
        children={
          'Ao retornar, todos os dados não salvos serão perdidos e será necessário importar um novo frete.'
        }
        onClickPublish={handlePrev}
        onCancelClick={() => setModalOpen(false)}
        ButtonOutlinePrimary="Não"
        ButtonPrimary="Sim"
      />
      {listReordered ? (
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AlertWarning
            isOpen={modalOpen}
            onCancelClick={() => setModalOpen(false)}
            onConfirmClick={handleRecalculedFreight}
            loading={isRecalculating}
          />
        </Modal>
      ) : (
        <ModalCustomizeFreight
          open={modalPublishOpen}
          onCancelClick={() => setModalPublishOpen(false)}
          onNextStep={handlePublishClick}
          onChangeSelectedValue={handleChangeSelectedValue}
          selectedValue={selectedValue}
        />
      )}
      <Box component={'div'}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography mb={2} fontWeight="700" color="secondary">
              Número da carga
            </Typography>
            <Controller
              name="loadNumber"
              control={control}
              render={() => (
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.loadNumber}
                </Typography>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography mb={2} fontWeight="700" color="secondary">
              Criado em
            </Typography>
            <Controller
              name="createdAt"
              control={control}
              render={() => (
                <Typography fontWeight="400" color="secondary">
                  {date.format('DD/MM/YYYY')}
                </Typography>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography mb={2} fontWeight="700" color="secondary">
              Tipo de veículo
            </Typography>
            <Controller
              name="vehicleType"
              control={control}
              render={({ field: { value } }) => (
                <Typography fontWeight="400" color="secondary">
                  {value || '--'}
                </Typography>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography mb={2} fontWeight="700" color="secondary">
              Data da retirada
            </Typography>
            <Controller
              name="pickUpDate"
              control={control}
              render={() => (
                <Typography fontWeight="400" color="secondary">
                  {moment(freightDetails.pickUpDate).format('DD/MM/YYYY')}
                </Typography>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography mb={2} fontWeight="700" color="secondary">
              Horário da retirada
            </Typography>
            <Controller
              name="pickUpTime"
              control={control}
              render={() => (
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.pickUpTime}
                </Typography>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography mb={2} fontWeight="700" color="secondary">
              Data limite de entrega
            </Typography>
            <Controller
              name="deliveryLimitDate"
              control={control}
              render={() => (
                <Typography fontWeight="400" color="secondary">
                  {moment(freightDetails.deliveryLimitDate).format(
                    'DD/MM/YYYY',
                  )}
                </Typography>
              )}
            />
          </Grid>

          {freightDetails.freightType !== freightTypeEnum.PUBLIC && (
            <>
              <Grid item xs={4}>
                <Typography mb={2} fontWeight="700" color="secondary">
                  Veículo
                </Typography>
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.vehicle?.model || '--'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography mb={2} fontWeight="700" color="secondary">
                  Placa do veículo
                </Typography>
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.vehicle?.plate || '--'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography mb={2} fontWeight="700" color="secondary">
                  Motorista
                </Typography>
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.driver?.username}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography mb={2} fontWeight="700" color="secondary">
                  Contato - Telefone
                </Typography>
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.driver?.phoneNumber}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography mb={2} fontWeight="700" color="secondary">
                  Contato - E-mail
                </Typography>
                <Typography fontWeight="400" color="secondary">
                  {freightDetails.driver?.email}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
        <hr />
        <Typography fontWeight="700" color="secondary">
          Cálculo do frete
        </Typography>

        <FreightCalculationDataTable />

        <hr />

        <ReorderRoute items={deliveriesToReorder} onReorder={handleReorder} />

        <Grid container justifyContent="space-between" style={{ marginTop: 5 }}>
          <ButtonComponent
            theme="outline-primary"
            justifyContent="flex-start"
            onClick={handleOpenModalBack}
          >
            Voltar
          </ButtonComponent>
          <ButtonComponent
            theme="primary"
            justifyContent="flex-end"
            onClick={handlePublicFreightAction}
            disabled={
              listReordered
                ? isRecalculating || configureFreightLoading
                : registerFreightLoading ||
                  updateFreightLoading ||
                  customPriceLoading
            }
            loading={
              listReordered
                ? isRecalculating || configureFreightLoading
                : registerFreightLoading ||
                  updateFreightLoading ||
                  customPriceLoading
            }
          >
            {listReordered ? 'Recalcular Frete' : 'Publicar frete'}
          </ButtonComponent>
        </Grid>
      </Box>
    </>
  );
};

export default CheckImport;
